import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import { Toaster } from "./components/ui/toaster";
import { QuoteModalProvider } from "./components/QuoteModal";
import FloatingSideCTA from "./components/FloatingSideCTA";
import { excelServicePages } from "./data/excelServicePages";

// Eagerly loaded — Home is the landing page and needs to appear fast
import Home from "./pages/Home";

// Route-level code splitting
const About           = lazy(() => import("./pages/About"));
const Blog            = lazy(() => import("./pages/Blog"));
const BlogPost        = lazy(() => import("./pages/BlogPost"));
const Contact         = lazy(() => import("./pages/Contact"));
const FAQs            = lazy(() => import("./pages/FAQs"));
const Sitemap         = lazy(() => import("./pages/Sitemap"));
const NotFound        = lazy(() => import("./pages/NotFound"));
const ServicePage     = lazy(() => import("./pages/ServicePage"));

// Legacy service pages, now at first-level URLs
const SEO             = lazy(() => import("./pages/SEO"));
const PPCAdWords      = lazy(() => import("./pages/PPCAdWords"));
const HTTPSMigration  = lazy(() => import("./pages/HTTPSMigration"));
const OnlineReputation = lazy(() => import("./pages/OnlineReputation"));

// Industry SEO pages
const SEOAutomotive   = lazy(() => import("./pages/SEOAutomotive"));
const SEOConstruction = lazy(() => import("./pages/SEOConstruction"));
const SEOFashion      = lazy(() => import("./pages/SEOFashion"));
const SEOFranchises   = lazy(() => import("./pages/SEOFranchises"));
const SEOHealthcare   = lazy(() => import("./pages/SEOHealthcare"));
const SEOLawyers      = lazy(() => import("./pages/SEOLawyers"));
const SEORetail       = lazy(() => import("./pages/SEORetail"));

/**
 * Central list of 301-style redirects from legacy URLs to their new canonical
 * first-level slugs. React Router can't emit server 301s, so these use
 * <Navigate replace> — the user's browser URL is rewritten immediately.
 */
const legacyRedirects = [
  // User-specified renames
  ["/services/seo",                   "/seo"],
  ["/services/local-seo",             "/local-seo"],
  ["/local-seo-services",             "/local-seo"],
  ["/services/ppc-adwords",           "/pay-per-click-ppc-adwords-management"],
  ["/small-business-seo",             "/seo-for-small-business"],
  ["/services/seo-small-business",    "/seo-for-small-business"],
  ["/enterprise-seo",                 "/enterprise-seo-services"],

  // /services/* flattening (non-overlapping)
  ["/services/https-migration",       "/https-migration"],
  ["/services/google-penalty-removal","/google-penalty-removal"],
  ["/services/online-reputation",     "/online-reputation-management"],
  ["/services/link-building",         "/link-building-services"],

  // Industry SEO flattening
  ["/services/seo-automotive",        "/seo-for-automotive"],
  ["/services/seo-construction",      "/seo-for/construction"],
  ["/services/seo-fashion",           "/seo-for-fashion"],
  ["/services/seo-franchises",        "/seo-for-franchises"],
  ["/services/seo-healthcare",        "/seo-for-healthcare"],
  ["/services/seo-lawyers",           "/seo-for-lawyers"],
  ["/services/seo-retail",            "/seo-for-retail"],

  // Legacy /service/* single routes
  ["/service/search-engine-optimisation", "/seo-services"],
  ["/service/google-ads-management",      "/google-ads-management"],
];

const RouteLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="h-10 w-10 rounded-full border-4 border-slate-200 border-t-[#15803d] animate-spin" />
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <QuoteModalProvider>
            <ScrollToTop />
            <Header />
            <Suspense fallback={<RouteLoader />}>
              <main id="main-content" role="main">
                <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about-us" element={<About />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/faqs" element={<FAQs />} />
                <Route path="/sitemap" element={<Sitemap />} />

                {/* Legacy service pages, re-homed at first level */}
                <Route path="/seo" element={<SEO />} />
                <Route path="/pay-per-click-ppc-adwords-management" element={<PPCAdWords />} />
                <Route path="/https-migration" element={<HTTPSMigration />} />
                <Route path="/online-reputation-management" element={<OnlineReputation />} />

                {/* Industry SEO (literal per user request) */}
                <Route path="/seo-for-automotive" element={<SEOAutomotive />} />
                <Route path="/seo-for/construction" element={<SEOConstruction />} />
                <Route path="/seo-for/construction/" element={<Navigate to="/seo-for/construction" replace />} />
                <Route path="/seo-for-fashion" element={<SEOFashion />} />
                <Route path="/seo-for-franchises" element={<SEOFranchises />} />
                <Route path="/seo-for-healthcare" element={<SEOHealthcare />} />
                <Route path="/seo-for-lawyers" element={<SEOLawyers />} />
                <Route path="/seo-for-retail" element={<SEORetail />} />

                {/* Excel-driven service routes — verbatim content from Google Docs */}
                {excelServicePages.map((page) => (
                  <Route key={page.slug} path={page.slug} element={<ServicePage />} />
                ))}

                {/* Legacy → new 301-style redirects */}
                {legacyRedirects.map(([from, to]) => (
                  <Route key={from} path={from} element={<Navigate to={to} replace />} />
                ))}

                {/* 404 catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </main>
            </Suspense>
            <Footer />
            <FloatingSideCTA />
            <Toaster />
          </QuoteModalProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
